import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createEmergencyVisit } from "../../../api/emergency";
import { useAuth } from "../../../context";
import {
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from "@mui/material";
import TicketSearch from "../../../Components/TicketSearch";
import TicketSelectionView from "../../../Components/TicketSelectionView";

export default function EmergencyCreate() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ triage_level: 2 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const auth = useAuth();
  const token = auth?.user?.token || "";

  // Auto-fill patient info when ticket is selected
  useEffect(() => {
    if (selectedTicket && selectedTicket.patient) {
      setForm((prev) => ({
        ...prev,
        patient: selectedTicket.patient,
        ticket: selectedTicket.id,
      }));
    } else {
      setForm((prev) => {
        const { patient, ticket, ...rest } = prev;
        return rest;
      });
    }
  }, [selectedTicket]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await createEmergencyVisit(form, token);
      setSuccessMessage(
        t("emergency.messages.visit_created", {
          defaultValue: "Emergency visit created successfully!",
        })
      );
      setTimeout(() => {
        navigate(`/emergency/${res.id}`);
      }, 1000);
    } catch (err) {
      console.error("Error creating emergency visit:", err);
      setError(
        t("emergency.errors.server_error", {
          defaultValue: "Failed to create emergency visit. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2, borderRadius: 2, boxShadow: 2 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
        >
          {t("emergency.buttons.new_visit", {
            defaultValue: "New Emergency Visit",
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("emergency.form.create_description", {
            defaultValue: "Create a new emergency visit record",
          })}
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
        <Grid container spacing={3} component="form" onSubmit={onSubmit}>
          {/* Optional Ticket Search */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "text.secondary" }}>
              {t("emergency.form.optionalTicket", { defaultValue: "Optional: Search by Ticket Number (for registered patients)" })}
            </Typography>
            <TicketSearch
              value={selectedTicket}
              onChange={setSelectedTicket}
              placeholder={t("emergency.placeholders.searchTicket", { defaultValue: "Search by ticket number (optional)..." })}
            />
          </Grid>

          {/* Show patient info if ticket is selected */}
          {selectedTicket && (
            <>
              <Grid item xs={12}>
                <TicketSelectionView ticket={selectedTicket} />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </>
          )}

          {/* Walk-in fields (only show if no ticket selected) */}
          {!selectedTicket && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("emergency.form.walkin_name", {
                    defaultValue: "Walk-in Name",
                  })}
                  name="walkin_name"
                  value={form.walkin_name || ""}
                  onChange={onChange}
                  placeholder={t("emergency.placeholders.enter_name", {
                    defaultValue: "Enter patient name...",
                  })}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("emergency.form.walkin_phone", {
                    defaultValue: "Phone Number",
                  })}
                  name="walkin_phone"
                  value={form.walkin_phone || ""}
                  onChange={onChange}
                  placeholder={t("emergency.placeholders.enter_phone", {
                    defaultValue: "Enter phone number...",
                  })}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("emergency.form.walkin_age", { defaultValue: "Age" })}
                  name="walkin_age"
                  type="number"
                  value={form.walkin_age || ""}
                  onChange={onChange}
                  placeholder={t("emergency.placeholders.enter_age", {
                    defaultValue: "Enter age...",
                  })}
                  variant="outlined"
                />
              </Grid>
            </>
          )}

          {/* Triage Level - Always show */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label={t("emergency.form.triage_level", {
                defaultValue: "Triage Level",
              })}
              name="triage_level"
              value={form.triage_level}
              onChange={onChange}
              variant="outlined"
            >
              <MenuItem value={1}>
                {t("emergency.triage.red", { defaultValue: "Red" })}
              </MenuItem>
              <MenuItem value={2}>
                {t("emergency.triage.yellow", { defaultValue: "Yellow" })}
              </MenuItem>
              <MenuItem value={3}>
                {t("emergency.triage.green", { defaultValue: "Green" })}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("emergency.form.chief_complaint", {
                defaultValue: "Chief Complaint",
              })}
              name="chief_complaint"
              value={form.chief_complaint || ""}
              onChange={onChange}
              placeholder={t("emergency.placeholders.enter_complaint", {
                defaultValue: "Enter chief complaint...",
              })}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                {t("emergency.buttons.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("emergency.buttons.new_visit", {
                    defaultValue: "Create Visit",
                  })
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
